// Converts profile rules to mammoth style-map lines (in automatic order) and validates them.
import { readStyle } from "mammoth/lib/style-reader.js";
import { orderRules } from "./order.js";

export const RUN_PROPERTIES = ["b", "i", "u", "strike", "small-caps", "all-caps", "highlight"];
export const LIST_MATCHES = [1, 2, 3, 4, 5].flatMap((level) => [`ul${level}`, `ol${level}`]);
export const STYLED_MATCHES = ["p", "r", "table"];
export const ALL_MATCHES = [...STYLED_MATCHES, ...LIST_MATCHES, ...RUN_PROPERTIES];

const CALLOUT_NAMES = ["note", "tip", "important", "warning", "caution"];

export function defaultSample(match, html = "", style = "") {
  if (match === "p") {
    const output = html.trim().toLowerCase();
    if (/^h[1-6]\b/.test(output)) return "heading";
    if (/^(blockquote|figure)\b/.test(output)) return "quote";
    if (/^pre\b/.test(output)) return "code";
    if (CALLOUT_NAMES.includes(style.trim().toLowerCase())) return "callout";
    return "paragraph";
  }
  if (match === "table") return "table";
  if (LIST_MATCHES.includes(match)) return "list";
  return "inline";
}

export function makeRule({ match, style, prefix, html = "", group = "main", sample, priority }) {
  const rule = { match };
  if (STYLED_MATCHES.includes(match) && typeof style === "string" && style !== "") {
    rule.style = style;
    if (prefix) rule.prefix = true;
  }
  rule.html = html;
  rule.group = group === "quiet" ? "quiet" : "main";
  rule.sample = sample ?? defaultSample(match, html, rule.style ?? "");
  if (priority === "first" || priority === "last") rule.priority = priority;
  return rule;
}

export function makeRawRule(raw, error) {
  return error ? { raw, error } : { raw };
}

export function ruleKey(rule) {
  if (rule.raw !== undefined) return `raw|${rule.raw}`;
  return `${rule.match}|${(rule.style ?? "").toLowerCase()}|${rule.prefix ? "^" : ""}`;
}

const escapeName = (name) => name.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
const unescapeName = (text) => text.replace(/\\(.)/g, "$1");

export function selectorFor(rule) {
  if (LIST_MATCHES.includes(rule.match)) {
    const kind = rule.match.startsWith("ul") ? "unordered" : "ordered";
    return `p:${kind}-list(${rule.match.slice(2)})`;
  }
  if (!rule.style) return rule.match;
  return `${rule.match}[style-name${rule.prefix ? "^=" : "="}'${escapeName(rule.style)}']`;
}

export function ruleToLine(rule) {
  if (rule.raw !== undefined) return rule.raw;
  const html = (rule.html ?? "").trim();
  return html ? `${selectorFor(rule)} => ${html}` : `${selectorFor(rule)} =>`;
}

export function orderedRules(profile) {
  return orderRules(profile.rules ?? []);
}

export function toStyleMap(profile) {
  return orderedRules(profile).map(ruleToLine);
}

const STYLED_RE = /^(p|r|table)(?:\[style-name(\^?=)'((?:\\.|[^'\\])*)'\])?\s*=>\s*(.*)$/;
const LIST_RE = /^p:(unordered|ordered)-list\(([1-5])\)\s*=>\s*(.*)$/;
const PROPERTY_RE = /^(b|i|u|strike|small-caps|all-caps|highlight)\s*=>\s*(.*)$/;

export function parseLine(line) {
  let m;
  if ((m = STYLED_RE.exec(line))) {
    const rule = { match: m[1] };
    if (m[3] !== undefined) {
      rule.style = unescapeName(m[3]);
      if (m[2] === "^=") rule.prefix = true;
    }
    rule.html = m[4].trim();
    return rule;
  }
  if ((m = LIST_RE.exec(line))) return { match: `${m[1] === "unordered" ? "ul" : "ol"}${m[2]}`, html: m[3].trim() };
  if ((m = PROPERTY_RE.exec(line))) return { match: m[1], html: m[2].trim() };
  return makeRawRule(line, "Not a mapping this editor understands; it is passed to mammoth unchanged.");
}

export function parseStyleMap(text, previousRules = []) {
  const previous = new Map(previousRules.map((rule) => [ruleKey(rule), rule]));
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("#"))
    .map((line) => {
      const parsed = parseLine(line);
      if (parsed.raw !== undefined) return parsed;
      const before = previous.get(ruleKey(parsed));
      return makeRule({ ...parsed, group: before?.group ?? "main", sample: before?.sample, priority: before?.priority });
    });
}

// Checks a rule with mammoth's own parser. Returns null when it's fine.
export function validateRule(rule) {
  if ((rule.match === "p" || rule.match === "r") && !rule.style) {
    return { field: "style", message: "Enter the Word style name." };
  }
  const html = (rule.html ?? "").trim();
  if (html.includes("<")) return { field: "html", message: "Use element names like div.box, not tags like <div>." };
  const line = ruleToLine(rule);
  const warning = readStyle(line).messages[0];
  if (!warning) return null;
  const at = /character number (\d+): (.*)$/s.exec(warning.message);
  if (!at) return { field: "html", message: "Mammoth can't read this output." };
  const position = Number(at[1]) - (line.length - html.length);
  if (position < 1) return { field: "style", message: `Mammoth can't read this style name: ${at[2]}.` };
  return { field: "html", message: `Mammoth can't read this output at character ${position}: ${at[2]}.` };
}
